/* FerryGate Widget — design system strict noir & blanc.
   Direction : minimalisme raffiné « guichet premium » (réf. Airbnb checkout /
   Google Flights). Fond gris doux dans le funnel, cartes blanches, une seule
   famille typo embarquée, grammaire d'écran unique (topbar + stepper +
   action bar), focus visibles partout. */

/* Typo embarquée : Plus Jakarta Sans (humaniste géométrique, proche du Cereal
   d'Airbnb). Chargée via le <style> injecté dans document.head — les
   @font-face ne se déclarent pas depuis un shadow root, mais une famille
   enregistrée dans le light DOM est utilisable PARTOUT, shadow inclus.
   Fallback system-ui si la police est bloquée (CSP hôte). */
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

/* ─── Tokens ─────────────────────────────────────────────────────────── */

/* Tokens : appliqués au shadow root (:host), au shell SaaS, ET aux conteneurs
   téléportés dans document.body hors shadow DOM (backdrop drawer + overlay funnel). */
:host, .fg-shell, .fg-drawer__backdrop, .fg-overlay {
  --fg-font: 'Plus Jakarta Sans', 'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif;

  --fg-ink: #101828;
  --fg-ink-muted: #667085;
  --fg-ink-faint: #98a2b3;
  --fg-paper: #ffffff;
  --fg-surface: #f7f8fa;
  --fg-line: #e7eaee;
  --fg-line-strong: #d0d5dd;
  --fg-error: #991b1b;
  --fg-error-bg: #fef2f2;

  --fg-radius-sm: 8px;
  --fg-radius: 10px;
  --fg-radius-lg: 14px;

  --fg-space-1: 4px;
  --fg-space-2: 8px;
  --fg-space-3: 12px;
  --fg-space-4: 16px;
  --fg-space-5: 20px;
  --fg-space-6: 24px;

  /* Élévation : 2 niveaux subtils (repos / hover) + chromes téléportés. */
  --fg-shadow-sm: 0 1px 2px rgba(16, 24, 40, 0.05);
  --fg-shadow-md: 0 2px 4px rgba(16, 24, 40, 0.05), 0 8px 24px rgba(16, 24, 40, 0.08);
  --fg-shadow-overlay: 0 -8px 32px rgba(16, 24, 40, 0.18);
  --fg-shadow-side: -8px 0 32px rgba(16, 24, 40, 0.18);

  /* Anneau de focus clavier — visible sur fond clair comme sur encre. */
  --fg-ring: 0 0 0 2px var(--fg-paper), 0 0 0 4px var(--fg-ink);
}

/* Focus clavier : un seul langage pour TOUT interactif (boutons, cartes,
   lignes cliquables, steppers). :focus-visible → pas d'anneau au clic. */
.fg-btn:focus-visible,
.fg-sailing:focus-visible,
.fg-tariff__btn:focus-visible,
.fg-place-row:focus-visible,
.fg-od__field:focus-visible,
.fg-search__card:focus-visible,
.fg-row:focus-visible,
.fg-chip:focus-visible,
.fg-stepper__btn:focus-visible,
.fg-counter__btn:focus-visible,
.fg-field input:focus-visible,
.fg-field select:focus-visible {
  outline: none;
  box-shadow: var(--fg-ring);
}

/* Confort : animations coupées si l'utilisateur le demande. Scopé aux
   conteneurs du widget — ce CSS est aussi injecté dans document.head (pour
   les éléments téléportés), un `*` nu fuiterait sur le site hôte. */
@media (prefers-reduced-motion: reduce) {
  :host *, .fg-shell *, .fg-overlay *, .fg-drawer__backdrop * {
    transition-duration: 0.01ms !important;
    animation-duration: 0.01ms !important;
  }
}

:host {
  all: initial;
  display: block;
  contain: layout style;
  font-family: var(--fg-font);
  color: var(--fg-ink);
  line-height: 1.5;
  font-size: 14px;
  -webkit-font-smoothing: antialiased;
  text-rendering: optimizeLegibility;
}

*, *::before, *::after { box-sizing: border-box; }

button { font-family: inherit; }

/* ─── Layout shell ───────────────────────────────────────────────────── */

.fg-shell {
  background: var(--fg-paper);
  color: var(--fg-ink);
  min-height: 100%;
}

/* ─── Buttons ────────────────────────────────────────────────────────── */

.fg-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--fg-space-2);
  height: 44px;
  padding: 0 20px;
  border-radius: var(--fg-radius);
  font-size: 14px;
  font-weight: 600;
  letter-spacing: -0.005em;
  cursor: pointer;
  border: 1.5px solid transparent;
  background: transparent;
  color: var(--fg-ink);
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease, transform 0.06s ease;
  white-space: nowrap;
}
.fg-btn:disabled { cursor: not-allowed; opacity: 0.45; }
.fg-btn:active:not(:disabled) { transform: translateY(1px); }

.fg-btn--primary {
  background: var(--fg-ink);
  color: var(--fg-paper);
  border-color: var(--fg-ink);
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.15), 0 4px 12px rgba(15, 23, 42, 0.12);
}
.fg-btn--primary:hover:not(:disabled) {
  background: #000000;
  border-color: #000000;
  color: var(--fg-paper);
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.20), 0 8px 20px rgba(15, 23, 42, 0.18);
}
.fg-btn--primary:disabled {
  background: var(--fg-line-strong);
  color: var(--fg-ink-faint);
  border-color: var(--fg-line-strong);
  opacity: 1;
  box-shadow: none;
  cursor: not-allowed;
}

.fg-btn--secondary {
  background: var(--fg-paper);
  color: var(--fg-ink);
  border-color: var(--fg-line);
}
.fg-btn--secondary:hover:not(:disabled) { border-color: var(--fg-ink); }

.fg-btn--ghost {
  background: transparent;
  color: var(--fg-ink-muted);
}
.fg-btn--ghost:hover:not(:disabled) { color: var(--fg-ink); }

.fg-btn--block { width: 100%; flex: 1; }

.fg-btn--lg {
  height: 52px;
  padding: 0 32px;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.01em;
  min-width: 220px;
}

/* Icon-only square button (back, close, etc.) */
.fg-btn--icon {
  width: 40px; height: 40px; padding: 0;
  background: var(--fg-paper);
  border: 1.5px solid var(--fg-line-strong);
  color: var(--fg-ink);
  border-radius: 10px;
  box-shadow: none;
}
.fg-btn--icon:hover:not(:disabled) {
  background: var(--fg-ink);
  border-color: var(--fg-ink);
  color: var(--fg-paper);
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.15);
}

/* ─── Grammaire d'écran du funnel ────────────────────────────────────────
   CHAQUE étape overlay (Résultats → Options → Coordonnées) partage :
   1. `.fg-page__header` : retour + titre/sous-titre + stepper de progression
   2. des blocs-cartes blanches sur fond gris doux
   3. `.fg-actionbar`   : barre sticky totale + CTA, identique partout.     */

/* Wrapper d'étape : header pleine largeur AU-DESSUS du layout 2 colonnes
   (jamais coincé dans la colonne principale → le titre ne wrappe plus contre
   le stepper). Même rythme vertical que .fg-results. */
.fg-page { display: flex; flex-direction: column; gap: var(--fg-space-4); }

.fg-page__header {
  display: flex;
  align-items: center;
  gap: var(--fg-space-3);
  flex-wrap: wrap;
  padding-bottom: var(--fg-space-2);
}
.fg-page__titles { display: flex; flex-direction: column; gap: 2px; min-width: 0; flex: 1; }
.fg-page__title {
  margin: 0;
  font-size: 22px;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: var(--fg-ink);
  line-height: 1.2;
}
.fg-page__subtitle {
  margin: 0;
  font-size: 13px;
  color: var(--fg-ink-muted);
  line-height: 1.4;
}

/* Rappel de la recherche (Results.vue) : trajet/dates/passagers/véhicule sur
   UNE SEULE ligne tronquée (ellipsis) + bouton "Modifier" fixe à droite —
   jamais de retour à la ligne, quelle que soit la largeur (mobile compris) :
   sur un écran étroit, c'est le détail le moins prioritaire (véhicule, en fin
   de ligne) qui disparaît sous l'ellipsis, pas la hauteur de la carte qui gonfle. */
.fg-recap {
  display: flex; align-items: center;
  gap: var(--fg-space-3);
  padding: 10px var(--fg-space-4);
  background: var(--fg-surface);
  border: 1px solid var(--fg-line);
  border-radius: var(--fg-radius);
  font-size: 12.5px;
}
.fg-recap__main {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  color: var(--fg-ink-muted);
}
.fg-recap__route { font-weight: 700; color: var(--fg-ink); }
.fg-recap__sep { color: var(--fg-ink-faint); }
.fg-recap__edit {
  flex-shrink: 0;
  white-space: nowrap;
  padding: 4px 10px;
  font: inherit; font-weight: 600; font-size: 12px; color: var(--fg-ink);
  background: var(--fg-paper); border: 1px solid var(--fg-line-strong); border-radius: 999px;
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease, color 0.15s ease;
}
.fg-recap__edit:hover { background: var(--fg-ink); color: var(--fg-paper); border-color: var(--fg-ink); }

/* Stepper de progression — discret, numéroté, état fait/courant/à venir. */
.fg-progress {
  display: flex;
  align-items: center;
  gap: 6px;
  list-style: none;
  margin: 0;
  padding: 0;
  flex-shrink: 0;
}
.fg-progress__item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.fg-progress__item + .fg-progress__item::before {
  content: '';
  width: 16px;
  height: 1.5px;
  background: var(--fg-line-strong);
  border-radius: 1px;
  margin-right: 6px;
}
.fg-progress__dot {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 1.5px solid var(--fg-line-strong);
  background: var(--fg-paper);
  color: var(--fg-ink-muted);
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
  flex-shrink: 0;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}
.fg-progress__label {
  font-size: 12px;
  font-weight: 600;
  color: var(--fg-ink-muted);
  white-space: nowrap;
}
.fg-progress__item.is-current .fg-progress__dot {
  background: var(--fg-ink);
  border-color: var(--fg-ink);
  color: var(--fg-paper);
}
.fg-progress__item.is-current .fg-progress__label { color: var(--fg-ink); font-weight: 700; }
.fg-progress__item.is-done .fg-progress__dot {
  background: var(--fg-paper);
  border-color: var(--fg-ink);
  color: var(--fg-ink);
}
/* Mobile : on ne garde que les pastilles (labels masqués sauf courante). */
@media (max-width: 640px) {
  .fg-progress { width: 100%; order: 3; }
  .fg-progress__item:not(.is-current) .fg-progress__label { display: none; }
}

/* Rappel véhicule (étape Coordonnées) — marque/modèle · dims · galerie. */
.fg-vehicle-recap {
  margin: 0;
  display: inline-flex; align-items: center; gap: 8px;
  align-self: flex-start;
  padding: 8px 12px;
  background: var(--fg-surface);
  border: 1px solid var(--fg-line);
  border-radius: var(--fg-radius-sm);
  font-size: 12.5px; font-weight: 600;
  color: var(--fg-ink);
}
.fg-vehicle-recap .fg-icon, .fg-vehicle-recap svg { color: var(--fg-ink-muted); flex-shrink: 0; }
.fg-vehicle-recap__logo { width: 20px; height: 20px; object-fit: contain; flex-shrink: 0; }

/* État « aucune disponibilité » — carte centrée, CTA nouvelle recherche. */
.fg-noresults {
  display: flex; flex-direction: column;
  align-items: center; text-align: center;
  gap: var(--fg-space-3);
  padding: 48px 24px;
  background: var(--fg-paper);
  border: 1px solid var(--fg-line);
  border-radius: var(--fg-radius-lg);
  box-shadow: var(--fg-shadow-sm);
}
.fg-noresults__icon {
  width: 52px; height: 52px;
  border-radius: 50%;
  display: inline-flex; align-items: center; justify-content: center;
  background: var(--fg-surface);
  color: var(--fg-ink-muted);
}
.fg-noresults__title {
  margin: 0;
  font-size: 17px; font-weight: 700;
  color: var(--fg-ink);
  letter-spacing: -0.01em;
}
.fg-noresults__text {
  margin: 0;
  max-width: 400px;
  font-size: 13px; color: var(--fg-ink-muted);
  line-height: 1.5;
}
.fg-noresults .fg-btn { margin-top: var(--fg-space-2); }

/* Skeleton de chargement (page Résultats pendant l'appel availability) */
.fg-skeleton { display: flex; flex-direction: column; gap: 14px; }
.fg-skeleton__label { margin: 0 0 2px; color: #64748b; font-size: 14px; font-weight: 500; }
.fg-skeleton__card {
  border: 1px solid #eef2f7; border-radius: 14px; padding: 16px; background: #fff;
  display: flex; flex-direction: column; gap: 12px;
}
.fg-skeleton__foot { display: flex; justify-content: space-between; gap: 12px; }
.fg-skeleton__bar {
  position: relative; overflow: hidden; border-radius: 8px; background: #eef2f7;
}
.fg-skeleton__bar--title { height: 16px; width: 62%; }
.fg-skeleton__bar--sub { height: 12px; width: 42%; }
.fg-skeleton__bar--pill { height: 36px; width: 46%; border-radius: 10px; }
.fg-skeleton__bar--price { height: 36px; width: 24%; border-radius: 10px; }
.fg-skeleton__bar::after {
  content: ''; position: absolute; inset: 0; transform: translateX(-100%);
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.65), transparent);
  animation: fg-shimmer 1.4s infinite;
}
@keyframes fg-shimmer { 100% { transform: translateX(100%); } }

/* État d'erreur sur la page Résultats (échec de l'appel availability) */
.fg-results-error {
  display: flex; flex-direction: column; align-items: center; text-align: center;
  gap: 10px; padding: 32px 16px;
}
.fg-results-error__icon { color: #e11d48; }
.fg-results-error__title { margin: 4px 0 0; font-size: 18px; font-weight: 700; color: #0f172a; }
.fg-results-error__text { margin: 0; color: #64748b; font-size: 14px; max-width: 340px; }
.fg-results-error__actions { display: flex; gap: 10px; margin-top: 8px; flex-wrap: wrap; justify-content: center; }

/* Action bar commune — sticky en bas de l'overlay, total + CTA.
   `--fg-overlay-bottom` (réglable par l'hôte, ex. hauteur d'une tab-bar fixe)
   relève la barre pour qu'elle ne soit pas masquée par le chrome de la page
   hôte ; défaut 0 + safe-area (encoche/home indicator). */
.fg-actionbar {
  position: sticky;
  bottom: calc(var(--fg-overlay-bottom, 0px) + env(safe-area-inset-bottom, 0px));
  z-index: 5;
  display: flex;
  align-items: center;
  gap: var(--fg-space-4);
  margin-top: var(--fg-space-2);
  padding: var(--fg-space-3) var(--fg-space-4);
  padding-bottom: calc(var(--fg-space-3) + env(safe-area-inset-bottom, 0px));
  background: var(--fg-paper);
  border: 1px solid var(--fg-line);
  border-radius: var(--fg-radius-lg);
  box-shadow: var(--fg-shadow-md);
}
.fg-actionbar__total {
  display: flex;
  flex-direction: column;
  gap: 1px;
  flex: 1;
  min-width: 0;
}
.fg-actionbar__total-label {
  font-size: 10px;
  font-weight: 700;
  color: var(--fg-ink-muted);
  text-transform: uppercase;
  letter-spacing: 0.08em;
}
.fg-actionbar__total-value {
  font-size: 20px;
  font-weight: 800;
  color: var(--fg-ink);
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.01em;
  line-height: 1.1;
}
.fg-actionbar__total-note {
  font-size: 10.5px;
  color: var(--fg-ink-faint);
}
/* Aucune dispo pour un groupe → note d'avertissement (bloque Continuer). */
.fg-actionbar__total-note--warn { color: #dc2626; font-weight: 600; }
/* État verrouillé du footer, affiché dès l'arrivée sur l'écran tant que
   traversée + place ne sont pas choisies (remplace le total, cf. Results.vue). */
.fg-actionbar__hint {
  display: inline-flex; align-items: center; gap: 6px;
  font-size: 12.5px; font-weight: 600; color: var(--fg-ink-muted);
}
.fg-actionbar__hint svg { flex-shrink: 0; color: var(--fg-ink-faint); }
.fg-actionbar .fg-btn--primary { min-width: 180px; }
@media (max-width: 640px) {
  .fg-actionbar { border-radius: var(--fg-radius); }
  .fg-actionbar .fg-btn--primary { min-width: 0; flex: 1; }
  .fg-actionbar__total { flex: 0 0 auto; }
}

/* ─── Form fields ────────────────────────────────────────────────────── */

.fg-field {
  display: flex;
  flex-direction: column;
  gap: 5px;
}
.fg-field__label {
  font-size: 12px;
  font-weight: 600;
  color: var(--fg-ink);
  letter-spacing: -0.005em;
  display: inline-flex;
  align-items: center;
}
.fg-field__req {
  color: #dc2626;
  font-weight: 700;
  margin-left: 3px;
}
.fg-field input, .fg-field select, .fg-field textarea {
  height: 44px;
  padding: 0 12px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 500;
  color: var(--fg-ink);
  background: var(--fg-paper);
  border: 1.5px solid var(--fg-line-strong);
  border-radius: var(--fg-radius-sm);
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
}
.fg-field input:hover:not(:focus):not(.is-invalid),
.fg-field select:hover:not(:focus):not(.is-invalid),
.fg-field textarea:hover:not(:focus):not(.is-invalid) {
  border-color: var(--fg-ink-muted);
}
.fg-field input:focus, .fg-field select:focus, .fg-field textarea:focus {
  border-color: var(--fg-ink);
  box-shadow: 0 0 0 3px rgba(15, 23, 42, 0.12);
}
.fg-field input.is-invalid,
.fg-field select.is-invalid,
.fg-field textarea.is-invalid {
  border-color: #dc2626 !important;
  box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.10) !important;
}
.fg-field__error {
  display: block;
  margin-top: 4px;
  font-size: 11.5px;
  font-weight: 500;
  color: #dc2626;
}

.fg-form {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: var(--fg-space-3);
}

/* ─── Radio inline ───────────────────────────────────────────────────── */

/* ─── Segmented control (direction A/R · aller simple) ──────────────────
   Les <input type=radio> restent dans le DOM (a11y, v-model) mais sont
   visuellement masqués ; l'état actif est piloté par la classe is-active. */
.fg-seg {
  display: inline-flex;
  padding: 3px;
  background: var(--fg-surface);
  border: 1px solid var(--fg-line);
  border-radius: 999px;
  gap: 2px;
}
.fg-seg__opt {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 7px 16px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  color: var(--fg-ink-muted);
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}
.fg-seg__opt:hover { color: var(--fg-ink); }
.fg-seg__opt.is-active {
  background: var(--fg-ink);
  color: var(--fg-paper);
  box-shadow: var(--fg-shadow-sm);
}
.fg-seg__opt input {
  position: absolute;
  opacity: 0;
  width: 1px; height: 1px;
  pointer-events: none;
}
.fg-seg__opt:has(input:focus-visible) { box-shadow: var(--fg-ring); }

/* ─── Counters (passengers, animals) ─────────────────────────────────── */

.fg-counter {
  display: inline-flex;
  align-items: center;
  gap: var(--fg-space-3);
}
.fg-counter__btn {
  width: 28px; height: 28px;
  display: inline-flex; align-items: center; justify-content: center;
  background: var(--fg-paper);
  border: 1px solid var(--fg-line);
  border-radius: var(--fg-radius-sm);
  color: var(--fg-ink);
  cursor: pointer;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
}
.fg-counter__btn:hover:not(:disabled) { background: var(--fg-ink); border-color: var(--fg-ink); color: var(--fg-paper); }
.fg-counter__btn:disabled { opacity: 0.35; cursor: not-allowed; }
.fg-counter__value { min-width: 20px; text-align: center; font-weight: 600; font-size: 14px; }

/* ─── Card row (clickable, hoverable list item) ──────────────────────── */

.fg-row {
  display: flex;
  align-items: center;
  gap: var(--fg-space-3);
  padding: var(--fg-space-3) var(--fg-space-4);
  background: var(--fg-paper);
  border: 1px solid var(--fg-line);
  border-radius: var(--fg-radius);
  cursor: pointer;
  text-align: left;
  width: 100%;
  font: inherit;
  color: inherit;
  transition: border-color 0.15s ease, background 0.15s ease;
}
.fg-row:hover { border-color: var(--fg-ink); }
.fg-row.is-selected { border-color: var(--fg-ink); background: var(--fg-surface); }

/* ─── Drawer (side panel) ────────────────────────────────────────────── */

.fg-drawer__backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.5);
  display: flex;
  justify-content: flex-end;
  align-items: stretch;
  z-index: 999999;
  font-family: var(--fg-font);
  color: var(--fg-ink);
  line-height: 1.5;
  font-size: 14px;
}
.fg-drawer__backdrop *, .fg-drawer__backdrop *::before, .fg-drawer__backdrop *::after { box-sizing: border-box; }
.fg-drawer__backdrop button { font-family: inherit; }
.fg-drawer {
  background: var(--fg-paper);
  width: min(440px, 100%);
  height: 100%;
  display: flex;
  flex-direction: column;
  box-shadow: var(--fg-shadow-side);
}
/* Mobile : drawer PLEIN ÉCRAN (expérience type app native) — header en haut,
   corps défilant, CTA épinglé en bas. Plus de demi-sheet partielle. */
.fg-drawer--sheet {
  width: 100%;
  height: 100%;
  max-height: 100%;
  margin-top: 0;
  border-radius: 0;
  box-shadow: none;
}
/* Safe-areas (encoche / home indicator) en plein écran mobile. */
.fg-drawer--sheet .fg-drawer__header { padding-top: calc(var(--fg-space-4) + env(safe-area-inset-top, 0px)); }
.fg-drawer--sheet .fg-drawer__footer { padding-bottom: calc(var(--fg-space-3) + env(safe-area-inset-bottom, 0px)); }
.fg-drawer__backdrop:has(.fg-drawer--sheet) {
  align-items: flex-end;
  justify-content: stretch;
}

.fg-drawer__header {
  display: flex;
  align-items: center;
  gap: var(--fg-space-3);
  padding: var(--fg-space-4) var(--fg-space-5);
  border-bottom: 1px solid var(--fg-line);
  flex-shrink: 0;
  background: var(--fg-paper);
}
.fg-drawer__title-wrap { flex: 1; min-width: 0; }
.fg-drawer__title {
  margin: 0;
  font-size: 17px;
  font-weight: 800;
  color: var(--fg-ink);
  letter-spacing: -0.015em;
}
.fg-drawer__subtitle {
  margin: 2px 0 0;
  font-size: 12px;
  color: var(--fg-ink-muted);
}

.fg-drawer__body {
  flex: 1;
  overflow-y: auto;
  padding: var(--fg-space-5);
  min-height: 0;
}

.fg-drawer__footer {
  flex-shrink: 0;
  padding: var(--fg-space-3) var(--fg-space-5);
  border-top: 1px solid var(--fg-line);
  display: flex;
  align-items: center;
  gap: var(--fg-space-3);
  background: var(--fg-paper);
}

/* Pile de boutons d'action (primaire + secondaire) empilés verticalement, pleine
   largeur. `flex:0 0 auto` neutralise le `flex:1` de `.fg-btn--block` (pensé pour
   un footer en ligne) → les boutons gardent leur hauteur naturelle, pas étirée. */
.fg-drawer__actions {
  display: flex; flex-direction: column;
  gap: var(--fg-space-2);
  width: 100%;
}
.fg-drawer__actions .fg-btn { flex: 0 0 auto; }

/* Drawer transitions */
.fg-drawer-overlay-enter-active,
.fg-drawer-overlay-leave-active { transition: opacity 180ms ease; }
.fg-drawer-overlay-enter-from,
.fg-drawer-overlay-leave-to { opacity: 0; }
.fg-drawer-side-enter-active,
.fg-drawer-side-leave-active { transition: transform 240ms cubic-bezier(0.2, 0.8, 0.2, 1); }
.fg-drawer-side-enter-from,
.fg-drawer-side-leave-to { transform: translateX(100%); }
.fg-drawer-sheet-enter-active,
.fg-drawer-sheet-leave-active { transition: transform 240ms cubic-bezier(0.2, 0.8, 0.2, 1); }
.fg-drawer-sheet-enter-from,
.fg-drawer-sheet-leave-to { transform: translateY(100%); }

/* ─── Search-style input ─────────────────────────────────────────────── */

.fg-search-input {
  width: 100%; height: 44px; padding: 0 var(--fg-space-4);
  font-family: inherit; font-size: 14px; color: var(--fg-ink);
  background: var(--fg-surface); border: 1.5px solid var(--fg-line-strong); border-radius: var(--fg-radius);
  outline: none; margin-bottom: var(--fg-space-3);
  transition: border-color 0.15s, box-shadow 0.15s, background 0.15s;
}
.fg-search-input::placeholder { color: var(--fg-ink-muted); }
.fg-search-input:focus { background: var(--fg-paper); border-color: var(--fg-ink); box-shadow: 0 0 0 2px rgba(15, 23, 42, 0.06); }

/* ─── Chip (filter buttons) ──────────────────────────────────────────── */

.fg-chip {
  display: inline-flex; align-items: center;
  height: 28px; padding: 0 var(--fg-space-3);
  background: var(--fg-paper); border: 1px solid var(--fg-line); border-radius: 14px;
  font-family: inherit; font-size: 12px; font-weight: 500; color: var(--fg-ink-muted);
  cursor: pointer; transition: background 0.15s, color 0.15s, border-color 0.15s;
}
.fg-chip:hover { border-color: var(--fg-ink); color: var(--fg-ink); }
.fg-chip.is-active { background: var(--fg-ink); color: var(--fg-paper); border-color: var(--fg-ink); }

/* ─── Vehicle drawer ─────────────────────────────────────────────────── */

.fg-veh__types {
  display: grid; grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
  gap: var(--fg-space-2);
}
.fg-veh__type {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: var(--fg-space-2);
  padding: var(--fg-space-4) var(--fg-space-2);
  background: var(--fg-paper); border: 1px solid var(--fg-line); border-radius: var(--fg-radius);
  cursor: pointer; transition: border-color 0.15s ease, background 0.15s ease;
  color: var(--fg-ink);
}
.fg-veh__type:hover { border-color: var(--fg-ink); }
.fg-veh__type.is-selected { border-color: var(--fg-ink); background: var(--fg-surface); }
.fg-veh__type-icon { color: var(--fg-ink); }
.fg-veh__type-label { font-size: 12px; font-weight: 600; color: var(--fg-ink); text-align: center; }

.fg-veh__list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: var(--fg-space-2); }
.fg-veh__brand-logo { width: 28px; height: 28px; display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; }
.fg-veh__brand-logo img { max-width: 100%; max-height: 100%; object-fit: contain; }
.fg-veh__brand-name { font-size: 14px; font-weight: 500; color: var(--fg-ink); flex: 1; }
.fg-veh__model-name { font-size: 14px; font-weight: 500; color: var(--fg-ink); flex: 1; }
.fg-veh__model-meta { font-size: 12px; color: var(--fg-ink-muted); }
.fg-veh__model-alias { color: var(--fg-ink-muted); }

.fg-veh__luggage { display: flex; flex-direction: column; gap: var(--fg-space-4); }

/* ─── Vehicle drawer · Banner récap (model/luggage steps) ──────────── */

.fg-veh-summary {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  margin-bottom: 16px;
  background: var(--fg-surface, #f8fafc);
  border: 1px solid var(--fg-line, #e5e7eb);
  border-radius: 12px;
}
.fg-veh-summary__logo {
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  padding: 4px;
  background: var(--fg-paper, #ffffff);
  border: 1px solid var(--fg-line, #e5e7eb);
  border-radius: 8px;
  object-fit: contain;
  filter: grayscale(1) contrast(1.05);
}
.fg-veh-summary__main { display: flex; flex-direction: column; gap: 6px; min-width: 0; flex: 1; }
.fg-veh-summary__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.fg-veh-summary__tag {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  background: var(--fg-paper, #ffffff);
  border: 1px solid var(--fg-line-strong, #d1d5db);
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  color: var(--fg-ink, #0f172a);
  letter-spacing: -0.005em;
  white-space: nowrap;
}
.fg-veh-summary__dims {
  display: flex;
  gap: 18px;
  font-size: 12.5px;
  color: var(--fg-ink-muted, #6b7280);
  flex-wrap: wrap;
}
.fg-veh-summary__dims strong {
  color: var(--fg-ink, #0f172a);
  font-weight: 600;
  margin-left: 4px;
}

/* ─── Vehicle drawer · Step "luggage" (galerie + caravane) ─────────── */

.fg-veh-luggage {
  display: flex;
  flex-direction: column;
  gap: 22px;
}
.fg-veh-luggage__section { display: flex; flex-direction: column; gap: 12px; }
.fg-veh-luggage__section-title {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--fg-ink, #0f172a);
}
.fg-veh-luggage__radios {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}
.fg-veh-luggage__radio {
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 12px 14px;
  background: var(--fg-paper, #ffffff);
  border: 1.5px solid var(--fg-line, #e5e7eb);
  border-radius: 10px;
  font-size: 13.5px;
  font-weight: 500;
  color: var(--fg-ink, #0f172a);
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
  text-align: left;
  font-family: inherit;
}
.fg-veh-luggage__radio:hover { border-color: var(--fg-line-strong, #d1d5db); }
.fg-veh-luggage__radio.is-active {
  border-color: var(--fg-ink, #0f172a);
  background: var(--fg-paper, #ffffff);
  box-shadow: 0 0 0 3px rgba(15, 23, 42, 0.08);
  font-weight: 600;
}
.fg-veh-luggage__dot {
  width: 16px; height: 16px;
  border-radius: 50%;
  border: 1.5px solid var(--fg-line-strong, #d1d5db);
  background: var(--fg-paper, #ffffff);
  flex-shrink: 0;
  position: relative;
  transition: border-color 0.15s ease;
}
.fg-veh-luggage__radio.is-active .fg-veh-luggage__dot {
  border-color: var(--fg-ink, #0f172a);
  background: var(--fg-ink, #0f172a);
}
.fg-veh-luggage__radio.is-active .fg-veh-luggage__dot::after {
  content: "";
  position: absolute;
  inset: 3px;
  border-radius: 50%;
  background: var(--fg-paper, #ffffff);
}

.fg-veh-luggage__group {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 14px;
  background: var(--fg-surface, #f8fafc);
  border-radius: 10px;
}
.fg-veh-luggage__group-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--fg-ink-muted, #6b7280);
}

.fg-veh-luggage__grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
}
@media (max-width: 480px) {
  .fg-veh-luggage__grid { grid-template-columns: repeat(2, 1fr); }
}
.fg-veh-luggage__chip {
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  padding: 10px 12px;
  background: var(--fg-paper, #ffffff);
  border: 1.5px solid var(--fg-line-strong, #d1d5db);
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  color: var(--fg-ink, #0f172a);
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease;
  font-family: inherit;
}
.fg-veh-luggage__chip:hover { border-color: var(--fg-ink, #0f172a); }
.fg-veh-luggage__chip.is-active {
  background: var(--fg-ink, #0f172a);
  color: var(--fg-paper, #ffffff);
  border-color: var(--fg-ink, #0f172a);
}
.fg-veh-luggage__chip.is-active .fg-veh-luggage__dot {
  border-color: var(--fg-paper, #ffffff);
  background: var(--fg-paper, #ffffff);
}
.fg-veh-luggage__chip.is-active .fg-veh-luggage__dot::after {
  background: var(--fg-ink, #0f172a);
}

.fg-veh-luggage__toggle {
  display: grid;
  grid-template-columns: 1fr 1fr;
  background: var(--fg-paper, #ffffff);
  border: 1.5px solid var(--fg-line-strong, #d1d5db);
  border-radius: 8px;
  overflow: hidden;
  padding: 3px;
  gap: 3px;
}
.fg-veh-luggage__toggle-btn {
  padding: 9px 12px;
  background: transparent;
  border: 0;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  color: var(--fg-ink, #0f172a);
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
  font-family: inherit;
}
.fg-veh-luggage__toggle-btn:hover:not(.is-active) { background: var(--fg-surface, #f8fafc); }
.fg-veh-luggage__toggle-btn.is-active {
  background: var(--fg-ink, #0f172a);
  color: var(--fg-paper, #ffffff);
}

.fg-veh-luggage__note {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 8px 0 0;
  padding: 10px 12px;
  background: var(--fg-surface, #f8fafc);
  border: 1px solid var(--fg-line, #e5e7eb);
  border-radius: 8px;
  font-size: 12px;
  color: var(--fg-ink-muted, #6b7280);
  line-height: 1.4;
}
.fg-veh-luggage__note :first-child { color: var(--fg-ink, #0f172a); flex-shrink: 0; }

/* ─── Journey drawer ─────────────────────────────────────────────────── */

.fg-jrn__item { gap: var(--fg-space-3); }
.fg-jrn__item-main { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 4px; }
.fg-jrn__route { display: flex; align-items: center; gap: var(--fg-space-2); font-size: 14px; font-weight: 600; color: var(--fg-ink); }
.fg-jrn__carrier { font-size: 11px; color: var(--fg-ink-muted); text-transform: capitalize; }

/* Sélecteur Origine / Destination (champs empilés + inverser) */
/* DÉPART et DESTINATION = deux cartes DISTINCTES (séparées par le gap), pas une
   carte fusionnée à filet : chaque champ se lit comme un bloc à part entière. */
.fg-od { position: relative; display: flex; flex-direction: column; gap: var(--fg-space-2); }
.fg-od__field {
  display: flex; flex-direction: column; gap: 2px; width: 100%; text-align: left;
  padding: var(--fg-space-3) var(--fg-space-4);
  background: var(--fg-paper);
  border: 1px solid var(--fg-line); border-radius: var(--fg-radius);
  font: inherit; color: inherit; cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
}
/* Réserve la place du bouton inverser (flottant dans le gap) sur les deux champs. */
.fg-od:has(.fg-od__swap) .fg-od__field { padding-right: 56px; }
.fg-od__field:hover:not(:disabled) { background: var(--fg-surface); }
.fg-od__field:disabled { cursor: default; }
/* Champ en cours d'édition : surligné + filet d'accent → l'user sait que la
   liste en dessous correspond à CE champ. */
.fg-od__field.is-active { background: var(--fg-surface); border-color: var(--fg-ink); box-shadow: inset 3px 0 0 var(--fg-ink); }
.fg-od__field-label { font-size: 11px; font-weight: 600; letter-spacing: 0.04em; text-transform: uppercase; color: var(--fg-ink-faint); }
.fg-od__field-value { font-size: 15px; font-weight: 600; color: var(--fg-ink); }
.fg-od__field-value.is-empty { font-weight: 500; color: var(--fg-ink-faint); }
.fg-od__swap {
  position: absolute; top: 50%; right: var(--fg-space-3); transform: translateY(-50%);
  width: 40px; height: 40px; display: grid; place-items: center; border-radius: 50%;
  background: var(--fg-paper); border: 1.5px solid var(--fg-line-strong); color: var(--fg-ink);
  cursor: pointer; box-shadow: var(--fg-shadow-sm); transition: border-color 0.15s ease;
}
.fg-od__swap:hover { border-color: var(--fg-ink); }
/* Effacer en haut, aligné à droite. */
.fg-od__toolbar { display: flex; justify-content: flex-end; margin-bottom: var(--fg-space-2); }
.fg-od__clear {
  border: 0; background: none; padding: 4px 2px; font: inherit; cursor: pointer;
  font-size: 13px; font-weight: 600; color: var(--fg-ink-muted); text-decoration: underline;
}
.fg-od__clear:hover { color: var(--fg-ink); }

/* Retour auto mis en évidence : chip avec icône + label fort. */
.fg-od__return {
  display: flex; align-items: center; gap: var(--fg-space-2);
  margin-top: var(--fg-space-3); padding: 10px var(--fg-space-3);
  background: var(--fg-surface); border: 1px solid var(--fg-line); border-radius: var(--fg-radius);
  font-size: 13px; color: var(--fg-ink);
}
.fg-od__return > svg { color: var(--fg-ink-muted); flex-shrink: 0; }
.fg-od__return-label { font-weight: 700; }
.fg-od__return-route { color: var(--fg-ink-muted); }

.fg-od__picker { margin-top: var(--fg-space-4); display: flex; flex-direction: column; gap: var(--fg-space-3); }
.fg-od__picker-label { margin: 0; font-size: 11px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; color: var(--fg-ink-faint); }
.fg-od__groups { display: flex; flex-direction: column; gap: var(--fg-space-4); }
.fg-od__group { display: flex; flex-direction: column; gap: var(--fg-space-2); }
.fg-od__group-title { margin: 0; font-size: 11px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; color: var(--fg-ink-faint); }
.fg-od__port { gap: var(--fg-space-3); }
.fg-od__port-main { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.fg-od__port-name { font-size: 14px; font-weight: 600; color: var(--fg-ink); }

/* ─── Place drawer ───────────────────────────────────────────────────── */

.fg-place__filters { display: flex; flex-wrap: wrap; gap: var(--fg-space-1); margin-bottom: var(--fg-space-3); }
.fg-place__item { gap: var(--fg-space-3); }
.fg-place__icon {
  width: 36px; height: 36px;
  display: inline-flex; align-items: center; justify-content: center;
  background: var(--fg-surface); border: 1px solid var(--fg-line); border-radius: var(--fg-radius-sm);
  color: var(--fg-ink); flex-shrink: 0;
}
.fg-place__item-text { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.fg-place__name { font-size: 14px; font-weight: 600; color: var(--fg-ink); }
.fg-place__meta { font-size: 11px; color: var(--fg-ink-muted); }
.fg-place__features { display: inline-flex; align-items: center; gap: var(--fg-space-2); color: var(--fg-ink-muted); flex-shrink: 0; }
.fg-place__price {
  font-size: 14px;
  font-weight: 700;
  color: var(--fg-ink);
  font-variant-numeric: tabular-nums;
  flex-shrink: 0;
  margin-left: var(--fg-space-2);
}

/* ─── Calendar drawer ────────────────────────────────────────────────── */

.fg-cal__tabs { display: grid; grid-template-columns: 1fr 1fr; gap: var(--fg-space-2); margin-bottom: var(--fg-space-4); }
.fg-cal__tab {
  display: flex; flex-direction: column; align-items: flex-start; gap: 4px;
  padding: var(--fg-space-3) var(--fg-space-3);
  background: var(--fg-paper); border: 1px solid var(--fg-line); border-radius: var(--fg-radius);
  cursor: pointer; text-align: left;
  transition: border-color 0.15s ease, background 0.15s ease;
}
.fg-cal__tab:hover:not(:disabled) { border-color: var(--fg-ink); }
.fg-cal__tab.is-active { border-color: var(--fg-ink); background: var(--fg-surface); }
.fg-cal__tab.is-disabled { opacity: 0.45; cursor: not-allowed; }
.fg-cal__tab-label { font-size: 10px; font-weight: 700; color: var(--fg-ink-muted); text-transform: uppercase; letter-spacing: 0.06em; }
.fg-cal__tab-value { font-size: 13px; font-weight: 600; color: var(--fg-ink); }

.fg-cal { display: flex; flex-direction: column; gap: var(--fg-space-3); }
.fg-cal__nav { display: flex; align-items: center; justify-content: space-between; gap: var(--fg-space-2); }
.fg-cal__nav-title { font-size: 14px; font-weight: 600; color: var(--fg-ink); text-transform: capitalize; flex: 1; text-align: center; }

.fg-cal__weekdays {
  display: grid; grid-template-columns: repeat(7, 1fr);
  font-size: 10px; font-weight: 600; color: var(--fg-ink-faint);
  text-transform: uppercase; letter-spacing: 0.04em;
  text-align: center; padding: var(--fg-space-1) 0;
}

.fg-cal__grid { display: grid; grid-template-columns: repeat(7, 1fr); gap: var(--fg-space-1); }
.fg-cal__cell {
  aspect-ratio: 1;
  display: inline-flex; align-items: center; justify-content: center;
  background: transparent; border: 1px solid transparent; border-radius: var(--fg-radius-sm);
  font: inherit; font-size: 13px; color: var(--fg-line-strong);
  cursor: not-allowed; padding: 0;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
}
.fg-cal__cell--available { color: var(--fg-ink); border-color: var(--fg-line); cursor: pointer; }
.fg-cal__cell--available:hover { border-color: var(--fg-ink); }
.fg-cal__cell--today { font-weight: 700; }
.fg-cal__cell--past { color: var(--fg-line); cursor: not-allowed; }
.fg-cal__cell--in-range { background: var(--fg-surface); border-color: transparent; color: var(--fg-ink); }
.fg-cal__cell--start, .fg-cal__cell--end { background: var(--fg-ink); color: var(--fg-paper); border-color: var(--fg-ink); font-weight: 700; }

/* Récap compact des choix de dates (aller + retour) + départs disponibles. */
.fg-cal__recap {
  margin-top: var(--fg-space-4);
  padding-top: var(--fg-space-4);
  border-top: 1px solid var(--fg-line);
  display: flex; flex-direction: column; gap: var(--fg-space-2);
}
.fg-cal__recap-leg {
  display: flex; flex-direction: column; gap: 6px;
  padding: 10px 12px;
  background: var(--fg-surface);
  border: 1px solid var(--fg-line);
  border-radius: var(--fg-radius);
}
.fg-cal__recap-leg.is-active { border-color: var(--fg-ink); box-shadow: inset 3px 0 0 var(--fg-ink); }
.fg-cal__recap-head { display: flex; align-items: center; gap: 6px; }
.fg-cal__recap-head > svg { color: var(--fg-ink-faint); flex-shrink: 0; }
.fg-cal__recap-label {
  font-size: 10px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase;
  color: var(--fg-ink-faint);
}
.fg-cal__recap-date { margin-left: auto; font-size: 13px; font-weight: 700; color: var(--fg-ink); }
.fg-cal__recap-date.is-muted { font-weight: 500; color: var(--fg-ink-faint); }
.fg-cal__recap-times { display: flex; flex-wrap: wrap; gap: 4px; }
.fg-cal__recap-time {
  font-size: 11px; font-weight: 600; color: var(--fg-ink-muted);
  background: var(--fg-paper); border: 1px solid var(--fg-line);
  border-radius: 999px; padding: 2px 8px; font-variant-numeric: tabular-nums;
}

/* Bouton « Ajouter un retour » (aller simple → aller-retour). */
.fg-cal__add-return {
  display: flex; align-items: center; justify-content: center; gap: 6px;
  width: 100%; margin-top: var(--fg-space-3); padding: 12px;
  background: var(--fg-paper); border: 1.5px dashed var(--fg-line-strong);
  border-radius: var(--fg-radius); cursor: pointer;
  font: inherit; font-size: 14px; font-weight: 600; color: var(--fg-ink);
  transition: border-color 0.15s ease, background 0.15s ease;
}
.fg-cal__add-return:hover { border-color: var(--fg-ink); background: var(--fg-surface); }

/* ─── Drawer body shared patterns (passengers/animals) ──────────────── */

.fg-pax { display: flex; flex-direction: column; gap: 0; }
.fg-pax__row {
  display: flex; align-items: center; justify-content: space-between;
  padding: var(--fg-space-3) 0;
  border-bottom: 1px solid var(--fg-line);
}
.fg-pax__row:last-child { border-bottom: 0; }
.fg-pax__label { font-size: 14px; font-weight: 500; color: var(--fg-ink); display: flex; flex-direction: column; gap: 2px; }
.fg-pax__band { font-size: 11px; font-weight: 500; color: var(--fg-ink-muted); }

.fg-pax__ages { margin-top: var(--fg-space-5); padding-top: var(--fg-space-5); border-top: 1px solid var(--fg-line); }
.fg-pax__ages-title { margin: 0 0 var(--fg-space-3); font-size: 11px; font-weight: 600; color: var(--fg-ink-muted); text-transform: uppercase; letter-spacing: 0.05em; }
.fg-pax__age-group { margin-bottom: var(--fg-space-3); padding: var(--fg-space-4); border: 1px solid var(--fg-line); border-radius: var(--fg-radius); }
.fg-pax__age-group:last-child { margin-bottom: 0; }
.fg-pax__age-group-header { font-size: 12px; font-weight: 600; color: var(--fg-ink); margin-bottom: var(--fg-space-3); }
.fg-pax__age-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(100px, 1fr)); gap: var(--fg-space-2); }

.fg-drawer__count { flex: 1; font-size: 13px; font-weight: 500; color: var(--fg-ink-muted); }

.fg-pax__pets { margin-top: var(--fg-space-5); padding-top: var(--fg-space-5); border-top: 1px solid var(--fg-line); }
.fg-pax__pets-link {
  display: inline-flex; align-items: center;
  background: transparent; border: 0; padding: 0;
  font: inherit; font-size: 13px; font-weight: 500;
  color: var(--fg-ink); cursor: pointer;
  text-decoration: underline; text-underline-offset: 3px;
  transition: color 0.15s ease;
}
.fg-pax__pets-link:hover { color: var(--fg-ink-muted); }

/* ─── Search screen ──────────────────────────────────────────────────── */

.fg-search { display: flex; flex-direction: column; gap: var(--fg-space-5); padding: var(--fg-space-5); }
.fg-search__header { display: flex; flex-direction: column; gap: 4px; }
.fg-search__title { margin: 0; font-size: 20px; font-weight: 700; color: var(--fg-ink); letter-spacing: -0.01em; }
.fg-search__subtitle { margin: 0; font-size: 13px; color: var(--fg-ink-muted); }

.fg-search__direction { display: flex; justify-content: center; gap: var(--fg-space-5); }

.fg-search__cards { display: flex; flex-direction: column; gap: var(--fg-space-2); }
.fg-search__cta { display: flex; justify-content: center; margin-top: var(--fg-space-2); }
@media (max-width: 480px) {
  .fg-search__cta { justify-content: stretch; }
  .fg-search__cta .fg-btn { width: 100%; min-width: 0; }
}
.fg-search__card {
  display: flex; align-items: center; gap: var(--fg-space-3);
  padding: 14px var(--fg-space-4);
  background: var(--fg-paper);
  border: 1px solid var(--fg-line);
  border-radius: var(--fg-radius);
  cursor: pointer; text-align: left; width: 100%;
  font: inherit; color: inherit;
  box-shadow: var(--fg-shadow-sm);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.fg-search__card:hover { border-color: var(--fg-ink); box-shadow: var(--fg-shadow-md); }
.fg-search__card-icon {
  width: 38px; height: 38px;
  display: inline-flex; align-items: center; justify-content: center;
  background: var(--fg-surface);
  border: 1px solid var(--fg-line);
  border-radius: var(--fg-radius-sm);
  color: var(--fg-ink);
  flex-shrink: 0;
}
.fg-search__card-text { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.fg-search__card-title { font-size: 11px; font-weight: 600; color: var(--fg-ink-muted); text-transform: uppercase; letter-spacing: 0.05em; }
.fg-search__card-value { font-size: 14px; font-weight: 600; color: var(--fg-ink); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.fg-search__card-chev { color: var(--fg-ink-faint); flex-shrink: 0; }
.fg-search__card-loader { color: var(--fg-ink); flex-shrink: 0; display: inline-flex; }
.fg-search__card-loader .fg-spinner__svg { animation: fg-spin 0.8s linear infinite; }
.fg-search__card-loader .fg-spinner__svg circle { stroke-dasharray: 90; stroke-dashoffset: 60; }
.fg-search__card:disabled { cursor: not-allowed; opacity: 0.75; }

/* ─── Results screen ─────────────────────────────────────────────────── */

/* Le padding d'écran est fourni UNE fois par .fg-overlay__panel — les écrans
   eux-mêmes n'en rajoutent pas (sinon marges doublées et rythme cassé). */
.fg-results { display: flex; flex-direction: column; gap: var(--fg-space-4); }

.fg-place-list {
  display: flex; flex-direction: column;
  gap: var(--fg-space-3);
}
.fg-place-list__filters { display: flex; gap: var(--fg-space-1); flex-wrap: wrap; }

@media (max-width: 720px) {
  .fg-results__split { grid-template-columns: 1fr; }
}

/* ─── Payment (Confirmation pre-paid state) ──────────────────────────── */

.fg-confirmation__head { margin-bottom: var(--fg-space-3); }

.fg-payment__amount {
  display: flex; justify-content: space-between; align-items: baseline;
  padding: 16px 18px;
  background: var(--fg-surface);
  border: 1px solid var(--fg-line);
  border-radius: var(--fg-radius);
}
.fg-payment__amount-label {
  font-size: 11px; font-weight: 700; color: var(--fg-ink-muted);
  text-transform: uppercase; letter-spacing: 0.08em;
}
.fg-payment__amount-value {
  font-size: 22px; font-weight: 700; color: var(--fg-ink);
  font-variant-numeric: tabular-nums; letter-spacing: -0.01em;
}

.fg-payment__methods {
  display: flex; flex-direction: column; gap: 10px;
}
.fg-payment__methods-title {
  margin: 0;
  font-size: 12px; font-weight: 700; color: var(--fg-ink-muted);
  text-transform: uppercase; letter-spacing: 0.06em;
}
.fg-payment__method {
  display: flex; align-items: center; gap: 12px;
  padding: 14px 16px;
  background: var(--fg-paper);
  border: 1px solid var(--fg-line);
  border-radius: var(--fg-radius);
  cursor: pointer;
  font: inherit; color: inherit; text-align: left;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.fg-payment__method:hover { border-color: var(--fg-ink-muted); }
.fg-payment__method.is-selected {
  border-color: var(--fg-ink);
  box-shadow: 0 0 0 1px var(--fg-ink);
}
.fg-payment__method-icon {
  width: 36px; height: 28px;
  display: inline-flex; align-items: center; justify-content: center;
  background: var(--fg-surface);
  border: 1px solid var(--fg-line);
  border-radius: 4px;
  font-size: 10px; font-weight: 700;
  color: var(--fg-ink-muted);
  flex-shrink: 0;
}
.fg-payment__method-body {
  flex: 1; min-width: 0;
  display: flex; flex-direction: column;
  gap: 2px;
}
.fg-payment__method-name {
  font-size: 13px; font-weight: 600; color: var(--fg-ink);
}
.fg-payment__method-desc {
  font-size: 11px; color: var(--fg-ink-muted);
}
.fg-payment__method-radio {
  width: 18px; height: 18px;
  border: 1.5px solid var(--fg-line-strong);
  border-radius: 50%;
  display: inline-flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.fg-payment__method.is-selected .fg-payment__method-radio {
  border-color: var(--fg-ink);
}
.fg-payment__method-radio-dot {
  width: 9px; height: 9px;
  background: var(--fg-ink);
  border-radius: 50%;
}

/* ─── Page with summary side panel ───────────────────────────────────── */

.fg-page-with-summary {
  display: grid;
  /* Découpe commune à TOUTES les étapes : 2/3 contenu + 1/3 récap. */
  grid-template-columns: minmax(0, 2fr) minmax(0, 1fr);
  gap: 28px;
  align-items: start;
  /* Pas de padding propre : la marge d'écran vient de .fg-overlay__panel. */
}
/* Results.vue masque l'aside (BookingSummary) tant qu'aucune traversée n'est
   choisie — pleine largeur pour ne pas laisser une colonne vide inutilement. */
.fg-page-with-summary--full { grid-template-columns: 1fr; }
.fg-page-with-summary__main {
  min-width: 0;
  display: flex; flex-direction: column;
  gap: var(--fg-space-3);
}
.fg-page-with-summary__aside {
  position: sticky;
  top: var(--fg-space-3);
  width: 100%;
}

@media (max-width: 1024px) {
  .fg-page-with-summary { gap: 20px; }
}
@media (max-width: 860px) {
  .fg-page-with-summary { grid-template-columns: 1fr; }
  /* Résumé empilé sous le contenu : on aère franchement la séparation. */
  .fg-page-with-summary__aside { position: static; margin-top: var(--fg-space-6); }
}

/* ─── Summary panel ──────────────────────────────────────────────────── */

/* Récap latéral (Options / Booking). Styles GLOBAUX (et non scoped) : le
   composant est téléporté hors shadow DOM, donc seules les règles injectées
   dans document.head s'appliquent. Tous les noms de classe ci-dessous suivent
   le template de BookingSummary.vue. */
.fg-summary {
  background: var(--fg-paper);
  border: 1px solid var(--fg-line);
  border-radius: var(--fg-radius-lg);
  overflow: hidden;
  box-shadow: var(--fg-shadow-sm);
}
.fg-summary__title {
  padding: 14px 16px;
  font-size: 14px; font-weight: 800; color: var(--fg-ink);
  letter-spacing: -0.01em;
  border-bottom: 1px solid var(--fg-line);
}
.fg-summary__empty {
  display: flex; flex-direction: column;
  align-items: center; gap: 8px;
  padding: 32px 20px;
  text-align: center;
  color: var(--fg-ink-faint);
  font-size: 12px;
}
.fg-summary__empty p { margin: 0; }

.fg-summary__section {
  padding: 14px 16px;
  border-bottom: 1px solid var(--fg-line);
  display: flex; flex-direction: column; gap: 6px;
}
.fg-summary__head {
  display: flex; align-items: center; gap: 6px;
  color: var(--fg-ink-faint);
  font-size: 10px; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.08em;
}
.fg-summary__route {
  margin: 0;
  display: flex; align-items: center; gap: 6px;
  flex-wrap: wrap;
  font-size: 15px; font-weight: 700; color: var(--fg-ink);
  letter-spacing: -0.005em; line-height: 1.25;
}
.fg-summary__route-arrow { color: var(--fg-ink-faint); flex-shrink: 0; }

.fg-summary__schedule {
  display: flex; flex-direction: column; gap: 4px;
  margin-top: 2px;
  padding: 8px 10px;
  background: var(--fg-surface);
  border-radius: var(--fg-radius-sm);
}
.fg-summary__schedule-row {
  display: flex; justify-content: space-between; align-items: baseline;
  gap: 12px;
}
.fg-summary__schedule-label {
  color: var(--fg-ink-muted);
  text-transform: uppercase; font-size: 10px; letter-spacing: 0.04em; font-weight: 700;
  white-space: nowrap;
}
.fg-summary__schedule-value {
  color: var(--fg-ink); font-weight: 500;
  font-size: 12px; font-variant-numeric: tabular-nums;
  text-align: right;
}
.fg-summary__duration { color: var(--fg-ink-faint); }

.fg-summary__meta {
  margin: 2px 0 0;
  display: flex; align-items: center; gap: 6px;
  font-size: 12px; color: var(--fg-ink-muted);
  flex-wrap: wrap;
}
.fg-summary__logo {
  width: 28px; height: 18px;
  object-fit: contain;
  background: var(--fg-surface);
  border: 1px solid var(--fg-line);
  border-radius: 3px; padding: 2px;
  flex-shrink: 0;
}
.fg-summary__dot { color: var(--fg-ink-faint); }

.fg-summary__text {
  margin: 0;
  font-size: 13px; color: var(--fg-ink);
}
/* Ligne véhicule : logo de marque + label alignés. */
.fg-summary__veh {
  display: flex; align-items: center; gap: 8px;
}
.fg-summary__veh-logo {
  width: 24px; height: 24px;
  object-fit: contain;
  flex-shrink: 0;
}

.fg-summary__price {
  display: flex; flex-direction: column; gap: 6px;
}
/* Détail prix groupé par sens (Aller / Retour). */
.fg-summary__price-group { display: flex; flex-direction: column; gap: 4px; }
.fg-summary__price-group + .fg-summary__price-group { margin-top: 6px; padding-top: 8px; border-top: 1px dashed var(--fg-line); }
.fg-summary__price-group-title {
  margin: 0; font-size: 10px; font-weight: 700; letter-spacing: 0.06em;
  text-transform: uppercase; color: var(--fg-ink-faint);
}
.fg-summary__price-row {
  display: flex; justify-content: space-between; align-items: baseline;
  gap: 12px;
  font-size: 13px; color: var(--fg-ink);
}
.fg-summary__price-label {
  display: inline-flex; align-items: baseline; gap: 4px; flex-wrap: wrap;
}
.fg-summary__price-sub { color: var(--fg-ink-faint); font-size: 11px; }
.fg-summary__price-amount {
  font-weight: 700; font-variant-numeric: tabular-nums; white-space: nowrap;
}
.fg-summary__price-note {
  margin: 2px 0 0;
  font-size: 11px; color: var(--fg-ink-faint); font-style: italic;
}

/* Prix d'un trajet, affiché DANS son bloc (Aller / Retour). */
.fg-summary__leg-price {
  display: flex; justify-content: space-between; align-items: baseline; gap: 12px;
  margin-top: 4px; padding-top: 8px; border-top: 1px solid var(--fg-line);
  font-size: 13px; color: var(--fg-ink);
}
.fg-summary__leg-price-label { color: var(--fg-ink-muted); min-width: 0; }
/* Repas rattachés au trajet : lignes secondaires (un peu plus discrètes que le tarif). */
.fg-summary__leg-price--meal { font-size: 12px; }
.fg-summary__leg-price--meal .fg-summary__price-amount { font-weight: 600; }

/* Sous-prix par groupe (dossier > 4 pax) sous le prix du trajet. */
.fg-summary__subprices {
  margin-top: 4px;
  display: flex; flex-direction: column; gap: 2px;
}
.fg-summary__subprice {
  display: flex; justify-content: space-between; align-items: baseline; gap: 12px;
  font-size: 11px; color: var(--fg-ink-faint);
}
.fg-summary__subprice-amount { font-variant-numeric: tabular-nums; }

.fg-summary__total {
  padding: 16px;
  display: flex; flex-direction: column; gap: 4px;
  background: var(--fg-surface);
}
.fg-summary__total-head { display: flex; justify-content: space-between; align-items: baseline; gap: 12px; }
.fg-summary__total-note { font-size: 11px; color: var(--fg-ink-faint); font-style: italic; }
.fg-summary__total-label {
  font-size: 11px; font-weight: 700; color: var(--fg-ink-muted);
  text-transform: uppercase; letter-spacing: 0.08em;
}
.fg-summary__total-value {
  font-size: 20px; font-weight: 700; color: var(--fg-ink);
  font-variant-numeric: tabular-nums; letter-spacing: -0.01em;
}
/* Rappel CTA en bas du résumé — plus petit que la barre d'action principale. */
.fg-summary__cta-wrap { padding: 12px 16px 16px; }
.fg-summary__cta { height: 46px; font-size: 14px; }

/* ─── Trip wizard (Results vertical steps) ───────────────────────────── */

/* ─── Bloc trajet (Results) : carte claire par trajet ────────────────── */
.fg-trip {
  display: flex; flex-direction: column;
  gap: var(--fg-space-4);
  padding: var(--fg-space-4);
  background: var(--fg-paper);
  border: 1px solid var(--fg-line);
  border-radius: var(--fg-radius-lg);
  box-shadow: var(--fg-shadow-sm);
}

/* En-tête de carte : pastille + libellé du bloc + route (Sète → Nador) ── */
.fg-trip__head {
  display: flex; align-items: center; gap: var(--fg-space-3);
  /* Bandeau : remonte sur les bords de la carte (annule le padding) pour
     démarquer nettement "Trajet aller/retour" du contenu (fond gris léger). */
  margin: calc(-1 * var(--fg-space-4)) calc(-1 * var(--fg-space-4)) 0;
  padding: var(--fg-space-3) var(--fg-space-4);
  background: var(--fg-surface);
  border-bottom: 1px solid var(--fg-line);
  border-radius: var(--fg-radius-lg) var(--fg-radius-lg) 0 0;
}
.fg-trip__head-icon {
  display: inline-flex; align-items: center; justify-content: center;
  width: 34px; height: 34px;
  border-radius: 10px;
  background: var(--fg-ink);
  color: var(--fg-paper);
  flex-shrink: 0;
  font-size: 16px; font-weight: 700; line-height: 1;
}
.fg-trip__head-icon--euro { font-size: 17px; }
.fg-trip__head-text { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.fg-trip__head-eyebrow {
  font-size: 11px; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.07em;
  color: var(--fg-ink-muted);
}
.fg-trip__head-route {
  display: inline-flex; align-items: center; gap: 8px;
  font-size: 15px; font-weight: 700;
  letter-spacing: -0.01em; color: var(--fg-ink);
  flex-wrap: wrap;
}
.fg-trip__head-route--sm { font-size: 13px; font-weight: 500; color: var(--fg-ink-muted); }
.fg-trip__head-arrow { color: var(--fg-ink-faint); font-weight: 500; }

/* Sous-étapes empilées à l'intérieur de la carte ────────────────────── */
.fg-trip__steps {
  display: flex; flex-direction: column;
  gap: var(--fg-space-5);
}

.fg-step {
  display: flex; flex-direction: column;
  gap: 10px;
  min-width: 0;
}
.fg-step__title {
  margin: 0;
  font-size: 12px; font-weight: 700; color: var(--fg-ink-muted);
  text-transform: uppercase; letter-spacing: 0.05em;
}
.fg-step__body {
  display: flex; flex-direction: column;
  gap: 16px; /* écart franc entre départs (ex. 09:00 vs 16:00) */
  min-width: 0;
}

/* Panneau qui s'ouvre EN PLACE sous le sailing sélectionné (choix de place) —
   rattaché visuellement à la carte au-dessus (collé, sans coins hauts), fond
   doux + filet d'accent à gauche : on lit un SOUS-bloc de l'horaire, pas une
   carte sœur. */
.fg-step__inline {
  display: flex; flex-direction: column;
  gap: 10px;
  margin-top: -16px; /* compense le gap du body : panneau collé à la carte */
  padding: 14px 14px 16px;
  background: var(--fg-surface);
  border: 1px solid var(--fg-ink);
  border-top: 0;
  border-radius: 0 0 var(--fg-radius-lg) var(--fg-radius-lg);
}
.fg-step__inline-title {
  margin: 0;
  font-size: 13px; font-weight: 700; color: var(--fg-ink);
}

/* ─── Liste des places en lignes-radio (sous-bloc de l'horaire) ───────── */
.fg-place-rows { display: flex; flex-direction: column; gap: var(--fg-space-2); }
.fg-place-row {
  display: flex; align-items: center; gap: var(--fg-space-3);
  width: 100%; text-align: left; padding: 10px var(--fg-space-3);
  background: var(--fg-paper); border: 1px solid var(--fg-line); border-radius: var(--fg-radius);
  cursor: pointer; font: inherit; color: inherit;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.fg-place-row:hover { border-color: var(--fg-ink-muted); }
.fg-place-row.is-selected { border-color: var(--fg-ink); box-shadow: 0 0 0 1px var(--fg-ink); }
.fg-place-row__radio {
  flex-shrink: 0; width: 18px; height: 18px; border-radius: 50%;
  border: 2px solid var(--fg-line-strong); position: relative; transition: border-color 0.15s ease;
}
.fg-place-row.is-selected .fg-place-row__radio { border-color: var(--fg-ink); }
.fg-place-row.is-selected .fg-place-row__radio::after {
  content: ''; position: absolute; inset: 3px; border-radius: 50%; background: var(--fg-ink);
}
.fg-place-row__icon {
  flex-shrink: 0; width: 30px; height: 30px; display: grid; place-items: center;
  border-radius: var(--fg-radius-sm); background: var(--fg-surface); color: var(--fg-ink-muted);
}
.fg-place-row__main { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 3px; }
/* Nom COMPLET, autorisé à passer sur 2 lignes (plus d'ellipse / troncature). */
.fg-place-row__name { font-size: 14px; font-weight: 600; color: var(--fg-ink); line-height: 1.25; }
.fg-place-row__kind {
  flex-shrink: 0; font-size: 10px; font-weight: 700; letter-spacing: 0.04em;
  text-transform: uppercase; color: var(--fg-ink-faint);
}
.fg-place-row__meta {
  display: flex; flex-wrap: wrap; align-items: center; gap: 2px 10px;
  font-size: 11px; color: var(--fg-ink-muted);
}
.fg-place-row__feat { display: inline-flex; align-items: center; gap: 3px; }
.fg-place-row__price { flex-shrink: 0; font-size: 14px; font-weight: 700; color: var(--fg-ink); }
/* La carte sélectionnée suivie du panneau perd ses coins bas : les deux ne
   forment qu'un seul bloc visuel. */
.fg-sailing.is-selected:has(+ .fg-step__inline) {
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
}

/* ─── Bloc Tarif (Results) ───────────────────────────────────────────── */

.fg-tariff {
  display: flex; flex-direction: column;
  gap: var(--fg-space-3);
}

/* Tarif unique : ligne récap simple */
.fg-tariff-readonly {
  margin: 0;
  display: flex; align-items: baseline; justify-content: space-between; gap: 12px;
  padding: 12px 14px;
  background: var(--fg-surface);
  border: 1px solid var(--fg-line);
  border-radius: var(--fg-radius);
  font-size: 13px; color: var(--fg-ink);
}
.fg-tariff-readonly strong { font-weight: 700; }
.fg-tariff-readonly__price {
  font-size: 16px; font-weight: 800; color: var(--fg-ink);
  font-variant-numeric: tabular-nums;
}

/* Sélecteur de type de tarif : cartes choisissables alignées à gauche */
.fg-tariff__options {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--fg-space-2);
}
.fg-tariff__btn {
  position: relative;
  display: flex; flex-direction: column; align-items: flex-start;
  gap: 3px;
  padding: 12px;
  background: var(--fg-paper);
  border: 1.5px solid var(--fg-line);
  border-radius: var(--fg-radius);
  cursor: pointer;
  font: inherit; color: inherit; text-align: left;
  transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
}
.fg-tariff__btn:hover { border-color: var(--fg-ink-muted); }
.fg-tariff__btn.is-active {
  border-color: var(--fg-ink);
  background: var(--fg-surface);
  box-shadow: inset 0 0 0 1px var(--fg-ink);
}
.fg-tariff__btn-check {
  position: absolute; top: 8px; right: 8px;
  display: inline-flex; align-items: center; justify-content: center;
  width: 16px; height: 16px;
  border-radius: 50%;
  background: var(--fg-ink); color: var(--fg-paper);
  opacity: 0; transform: scale(0.6);
  transition: opacity 0.15s, transform 0.15s;
}
.fg-tariff__btn.is-active .fg-tariff__btn-check { opacity: 1; transform: scale(1); }
.fg-tariff__btn-label {
  font-size: 11px; font-weight: 700; color: var(--fg-ink-muted);
  text-transform: uppercase; letter-spacing: 0.04em;
  padding-right: 18px;
}
.fg-tariff__btn.is-active .fg-tariff__btn-label { color: var(--fg-ink); }
.fg-tariff__btn-price {
  font-size: 17px; font-weight: 800; color: var(--fg-ink);
  font-variant-numeric: tabular-nums; letter-spacing: -0.01em;
}
.fg-tariff__btn-balance {
  font-size: 11px; color: var(--fg-ink-muted);
  font-variant-numeric: tabular-nums;
}

/* Encart détail paiement (prévente / option) — monochrome, sobre */
.fg-tariff__detail {
  border: 1px solid var(--fg-line);
  border-left: 3px solid var(--fg-ink);
  background: var(--fg-surface);
  border-radius: var(--fg-radius);
  padding: 14px 16px;
}
.fg-tariff__detail-head {
  display: flex; align-items: center; gap: 6px;
  margin-bottom: 10px;
  font-size: 12px; font-weight: 700; color: var(--fg-ink);
  text-transform: uppercase; letter-spacing: 0.05em;
}
.fg-tariff__detail-head-icon { color: var(--fg-ink-muted); flex-shrink: 0; }
.fg-tariff__detail-list {
  display: flex; flex-direction: column; gap: 0;
  margin: 0;
}
.fg-tariff__row {
  display: flex; justify-content: space-between; align-items: baseline;
  gap: 8px;
  padding: 8px 0;
  border-top: 1px solid var(--fg-line);
}
.fg-tariff__row:first-child { border-top: none; }
.fg-tariff__row dt { font-size: 13px; color: var(--fg-ink-muted); margin: 0; }
.fg-tariff__row dd {
  font-size: 13px; font-weight: 600; color: var(--fg-ink);
  font-variant-numeric: tabular-nums; margin: 0;
}
.fg-tariff__row--main {
  padding: 8px 10px;
  margin: 0 -10px 2px;
  background: var(--fg-paper);
  border: 1px solid var(--fg-line);
  border-radius: 6px;
  border-top: none;
}
.fg-tariff__row--main dt { font-size: 13px; font-weight: 700; color: var(--fg-ink); }
.fg-tariff__row--main dd { font-size: 18px; font-weight: 800; color: var(--fg-ink); }
.fg-tariff__detail-desc {
  margin: 10px 0 0;
  padding-top: 10px;
  border-top: 1px solid var(--fg-line);
  font-size: 11.5px; color: var(--fg-ink-muted);
  line-height: 1.5;
}

@media (max-width: 480px) {
  .fg-tariff__options { grid-template-columns: 1fr; }
}

/* ─── Sailing card (Results) ─────────────────────────────────────────── */

.fg-sailing {
  position: relative;
  display: flex; align-items: stretch;
  background: var(--fg-paper);
  border: 1px solid var(--fg-line);
  border-radius: var(--fg-radius);
  padding: 12px 14px;
  cursor: pointer; text-align: left;
  font: inherit; color: inherit;
  box-shadow: var(--fg-shadow-sm);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.fg-sailing:hover { border-color: var(--fg-ink-muted); box-shadow: var(--fg-shadow-md); }
.fg-sailing.is-selected { border-color: var(--fg-ink); box-shadow: 0 0 0 1px var(--fg-ink), var(--fg-shadow-sm); }

.fg-sailing__left {
  flex: 1; min-width: 0;
  display: flex; flex-direction: column;
  gap: 10px;
}

.fg-sailing__head {
  display: flex; align-items: center; gap: 10px;
  min-width: 0;
}
.fg-sailing__logo {
  width: 34px; height: 34px;
  object-fit: contain;
  flex-shrink: 0;
}
.fg-sailing__id {
  display: flex; flex-direction: column; gap: 1px;
  min-width: 0;
}
/* Compagnie = info primaire (carrier) ; nom du bateau = détail discret. */
.fg-sailing__company {
  font-size: 14px; font-weight: 700; color: var(--fg-ink);
  letter-spacing: 0.01em;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.fg-sailing__ship {
  display: inline-flex; align-items: center; gap: 4px;
  font-size: 11px; font-weight: 500; color: var(--fg-ink-faint);
  letter-spacing: 0.02em;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
/* Icône navire : donne le contexte "c'est le bateau" devant le nom (sinon le
   libellé seul, ex. "Cecilia payne", est cryptique). */
.fg-sailing__ship-icon { flex-shrink: 0; opacity: 0.65; }
.fg-sailing__icons {
  margin-left: auto;
  display: inline-flex; align-items: center; gap: 8px;
  color: var(--fg-ink-muted);
  flex-shrink: 0;
}

/* Badge "MEILLEUR PRIX" / "LE PLUS RAPIDE" — un seul à la fois (priorité prix),
   affiché uniquement s'il y a ≥2 horaires à comparer (cf. Results.vue). Petit
   et neutre : sert de repère de tri, ne doit pas dominer la carte. */
.fg-sailing__badge {
  margin-left: auto;
  flex-shrink: 0;
  display: inline-flex; align-items: center;
  padding: 3px 8px;
  font-size: 9.5px; font-weight: 700;
  letter-spacing: 0.03em;
  border-radius: 999px;
  white-space: nowrap;
}
.fg-sailing__badge--cheapest { background: #F0FDF4; color: #15803D; }
.fg-sailing__badge--fastest { background: #EFF6FF; color: #1D4ED8; }

/* Layout vertical : heure de départ au-dessus, puis la ligne de trajet
   (port départ ── durée ── port arrivée) en dessous. */
.fg-sailing__times {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}
/* Heure de départ = signal primaire (gros, gras), seule au-dessus du trajet. */
.fg-sailing__time {
  font-size: 19px;
  font-weight: 700;
  color: var(--fg-ink);
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.01em;
  line-height: 1.1;
  display: inline-flex;
  align-items: center;
  gap: 5px;
}
/* Trajet : les deux ports partagent la même ligne de base, reliés par le rail.
   align-items: center → la bulle durée est centrée verticalement face aux ports. */
.fg-sailing__route {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  column-gap: 12px;
  min-width: 0;
}
/* Ports = signal du trajet, lisibles et alignés sur la même ligne. */
.fg-sailing__port {
  font-size: 14px;
  font-weight: 600;
  color: var(--fg-ink);
  letter-spacing: 0;
  text-transform: capitalize;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}
.fg-sailing__port--to { text-align: right; }
.fg-sailing__moon { color: var(--fg-ink-muted); }

.fg-sailing__rail {
  position: relative;
  /* aligné sur la hauteur de la ligne d'heure (.fg-sailing__time ≈ 21px) pour
     que la bulle « durée » soit centrée face à l'heure de départ. */
  height: 21px;
  display: flex; align-items: center; justify-content: center;
}
.fg-sailing__rail::before {
  content: '';
  position: absolute;
  left: 0; right: 0; top: 50%;
  height: 1px; transform: translateY(-50%);
  background: repeating-linear-gradient(to right, var(--fg-line-strong) 0 3px, transparent 3px 6px);
}
.fg-sailing__rail-bubble {
  position: relative;
  background: var(--fg-paper);
  padding: 0 6px;
  display: inline-flex; align-items: center; gap: 4px;
  font-size: 11px; font-weight: 600; color: var(--fg-ink-muted);
  font-variant-numeric: tabular-nums;
  line-height: 1;
}

.fg-sailing__right {
  flex-shrink: 0;
  display: flex; flex-direction: column;
  align-items: flex-end; justify-content: center;
  gap: 3px;
  padding-left: 14px;
  margin-left: 14px;
  border-left: 1px solid var(--fg-line);
  min-width: 100px;
}
.fg-sailing__price {
  display: inline-flex; align-items: center; gap: 6px;
  font-size: 18px; font-weight: 700; color: var(--fg-ink);
  font-variant-numeric: tabular-nums; letter-spacing: -0.01em;
  line-height: 1;
}
.fg-sailing__price-meta {
  font-size: 10px; color: var(--fg-ink-faint);
  white-space: nowrap;
}
/* Affordance "Choisir" : signale que la carte est sélectionnable (sinon elle
   se lit comme une fiche d'info statique). Décoratif (le clic est porté par la
   carte = .fg-sailing) → un <span>, pas un <button> imbriqué. */
.fg-sailing__choose {
  margin-top: 6px;
  display: inline-flex; align-items: center; gap: 2px;
  padding: 5px 8px 5px 12px;
  font-size: 12px; font-weight: 600; color: var(--fg-ink);
  background: var(--fg-surface);
  border: 1px solid var(--fg-line-strong);
  border-radius: 999px; white-space: nowrap;
  transition: background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}
.fg-sailing:hover .fg-sailing__choose {
  background: var(--fg-ink); color: var(--fg-paper); border-color: var(--fg-ink);
}
/* Prix "à partir de" — volontairement discret (petit, muted, aucun poids
   visuel proche de `.fg-sailing__price` ci-dessus) : c'est un repère de
   comparaison entre horaires, pas une valeur finale à mettre en avant. */
.fg-sailing__from {
  font-size: 11px; font-weight: 500; color: var(--fg-ink-faint);
  white-space: nowrap;
}

/* Compteur "X traversées disponibles" — affiché seulement si ≥2 horaires. */
.fg-results-count {
  margin: 0; font-size: 11px; font-weight: 700; color: var(--fg-ink-faint);
  text-transform: uppercase; letter-spacing: 0.05em;
}

/* Hint d'aide affiché tant qu'aucun horaire n'est choisi pour la direction. */
.fg-step__hint {
  display: inline-flex; align-items: center; gap: 6px;
  margin: 0; font-size: 12.5px; font-weight: 500; color: var(--fg-ink-muted);
}
.fg-step__hint svg { flex-shrink: 0; color: var(--fg-ink-faint); }

@media (max-width: 600px) {
  .fg-sailing { flex-direction: column; padding: 14px; }
  .fg-sailing__right {
    flex-direction: row; align-items: center; justify-content: space-between;
    padding-left: 0; margin-left: 0; padding-top: 10px;
    border-left: 0; border-top: 1px solid var(--fg-line);
    width: 100%; min-width: 0;
  }
  /* En ligne (mobile) : "Choisir" calé à droite, sans décalage vertical. */
  .fg-sailing__choose { margin-top: 0; margin-left: auto; }
}

/* ─── Options screen ─────────────────────────────────────────────────── */

.fg-options {
  display: flex;
  flex-direction: column;
  gap: var(--fg-space-4);
}

/* ─── Groupe d'options par direction ────────────────────────────────── */
.fg-options-group {
  background: var(--fg-paper);
  border: 1px solid var(--fg-line);
  border-radius: var(--fg-radius-lg);
  overflow: hidden;
  box-shadow: var(--fg-shadow-sm);
}
/* En-tête de bloc identique à celui des Résultats (fg-trip__head) pour la
   cohérence de bout en bout du funnel : pastille carrée noire + eyebrow + route. */
.fg-options-group__head {
  display: flex;
  align-items: center;
  gap: var(--fg-space-3);
  padding: 14px 16px;
  border-bottom: 1px solid var(--fg-line);
}
.fg-options-group__icon {
  width: 34px; height: 34px;
  border-radius: 10px;
  background: var(--fg-ink);
  color: var(--fg-paper);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.fg-options-group__title-wrap { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.fg-options-group__eyebrow {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: var(--fg-ink-muted);
}
.fg-options-group__route {
  margin: 0;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: var(--fg-ink);
}
.fg-options-group__arrow {
  color: var(--fg-ink-faint);
  font-weight: 500;
}

.fg-options-group__list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
}

/* ─── Option card row ────────────────────────────────────────────────── */
.fg-option-card {
  display: grid;
  grid-template-columns: 40px minmax(0, 1fr) auto;
  gap: 12px;
  align-items: center;
  padding: 14px 16px;
  background: var(--fg-paper, #ffffff);
  transition: background 0.15s ease;
}
.fg-option-card + .fg-option-card {
  border-top: 1px solid var(--fg-line, #e5e7eb);
}
.fg-option-card.is-selected {
  background: var(--fg-surface, #f8fafc);
}
.fg-option-card__icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--fg-surface, #f8fafc);
  border: 1px solid var(--fg-line, #e5e7eb);
  color: var(--fg-ink, #0f172a);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.fg-option-card__main {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}
.fg-option-card__name {
  font-size: 14px;
  font-weight: 600;
  color: var(--fg-ink, #0f172a);
  letter-spacing: -0.005em;
  text-transform: none;
}
.fg-option-card__kind {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 7px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  vertical-align: middle;
  background: var(--fg-accent-soft, #f4f4f5);
  color: var(--fg-accent, #18181b);
}
.fg-option-card__desc {
  font-size: 12px;
  color: var(--fg-ink-muted, #6b7280);
  line-height: 1.4;
}
.fg-option-card__right {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-shrink: 0;
}
.fg-option-card__price {
  font-size: 14px;
  font-weight: 700;
  color: var(--fg-ink, #0f172a);
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.005em;
  white-space: nowrap;
}

/* ─── Stepper (+/-) ──────────────────────────────────────────────────── */
.fg-stepper {
  display: inline-flex;
  align-items: center;
  gap: 0;
  background: var(--fg-paper, #ffffff);
  border: 1.5px solid var(--fg-line-strong, #d1d5db);
  border-radius: 999px;
  overflow: hidden;
}
.fg-stepper__btn {
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--fg-ink, #0f172a);
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
  font-family: inherit;
  line-height: 1;
}
.fg-stepper__btn:hover:not(:disabled) {
  background: var(--fg-ink, #0f172a);
  color: var(--fg-paper, #ffffff);
}
.fg-stepper__btn:disabled {
  color: var(--fg-ink-faint, #9ca3af);
  cursor: not-allowed;
}
.fg-stepper__value {
  min-width: 28px;
  text-align: center;
  font-size: 13px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: var(--fg-ink, #0f172a);
}

.fg-empty {
  padding: var(--fg-space-5);
  text-align: center;
  color: var(--fg-ink-muted);
  font-size: 13px;
  background: var(--fg-surface);
  border: 1px solid var(--fg-line);
  border-radius: var(--fg-radius);
}

/* ─── Booking screen ─────────────────────────────────────────────────── */

.fg-booking { display: flex; flex-direction: column; gap: 12px; }
.fg-booking__hint {
  margin: -2px 0 0;
  font-size: 12px;
  font-weight: 500;
  color: var(--fg-ink-muted);
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.fg-booking__hint::before {
  content: "*";
  color: #dc2626;
  font-weight: 700;
}
.fg-booking__section {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--fg-ink-muted);
  margin: 8px 0 -2px;
}

/* PaxForm — carte plate alignée sur le récap (en-tête + corps, bordure 1px) */
.fg-pax-form {
  border: 1px solid var(--fg-line);
  border-radius: var(--fg-radius);
  margin: 0;
  background: var(--fg-paper);
  overflow: hidden;
}
.fg-pax-form__head {
  display: flex;
  align-items: center;
  gap: var(--fg-space-2);
  padding: 9px 14px;
  background: var(--fg-surface);
  border-bottom: 1px solid var(--fg-line);
}
.fg-pax-form__title {
  font-size: 12.5px;
  font-weight: 700;
  letter-spacing: -0.005em;
  color: var(--fg-ink);
}
/* Rappel de la tranche d'âge admissible du transporteur, à côté du type. */
.fg-pax-form__band {
  font-size: 10.5px;
  font-weight: 500;
  color: var(--fg-ink-muted);
  background: var(--fg-paper);
  border: 1px solid var(--fg-line);
  padding: 1px 6px;
  border-radius: 4px;
  white-space: nowrap;
}
.fg-pax-form__badge {
  margin-left: auto;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--fg-ink-muted);
  background: var(--fg-paper);
  border: 1px solid var(--fg-line-strong);
  border-radius: 999px;
  padding: 2px 9px;
}
.fg-pax-form__body { padding: 14px; }

/* ─── Confirmation screen ────────────────────────────────────────────── */

.fg-confirmation { display: flex; flex-direction: column; align-items: center; gap: var(--fg-space-4); padding: var(--fg-space-6) var(--fg-space-5); text-align: center; }
.fg-confirmation__icon {
  width: 56px; height: 56px;
  display: inline-flex; align-items: center; justify-content: center;
  background: var(--fg-ink); color: var(--fg-paper);
  border-radius: 50%;
  margin-bottom: var(--fg-space-2);
}
.fg-confirmation__title { margin: 0; font-size: 22px; font-weight: 700; color: var(--fg-ink); letter-spacing: -0.01em; }
.fg-confirmation__subtitle { margin: 0; font-size: 13px; color: var(--fg-ink-muted); }
.fg-confirmation__details { width: 100%; display: flex; flex-direction: column; gap: var(--fg-space-2); margin-top: var(--fg-space-3); }
.fg-ticket {
  display: flex; flex-direction: column; gap: 4px;
  padding: var(--fg-space-4);
  background: var(--fg-surface);
  border: 1px solid var(--fg-line);
  border-radius: var(--fg-radius);
  text-align: left;
}
.fg-ticket__route { font-size: 14px; font-weight: 600; color: var(--fg-ink); }
.fg-ticket__date { font-size: 12px; color: var(--fg-ink-muted); }
.fg-ticket__company { font-size: 11px; color: var(--fg-ink-muted); text-transform: capitalize; }
.fg-confirmation__actions { display: flex; gap: var(--fg-space-3); width: 100%; margin-top: var(--fg-space-3); }

/* ─── Error banner ───────────────────────────────────────────────────── */

.fg-error {
  display: flex; align-items: flex-start; gap: var(--fg-space-3);
  padding: var(--fg-space-3) var(--fg-space-4);
  background: var(--fg-error-bg);
  border: 1px solid var(--fg-error);
  border-radius: var(--fg-radius);
  color: var(--fg-error);
}
.fg-error__icon { flex-shrink: 0; margin-top: 1px; }
.fg-error__content { flex: 1; min-width: 0; }
.fg-error__code { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; margin-bottom: 2px; }
.fg-error__message { font-size: 13px; word-break: break-word; }
.fg-error__close {
  flex-shrink: 0; background: transparent; border: 0; color: inherit;
  cursor: pointer; padding: 0; display: inline-flex; opacity: 0.7;
}
.fg-error__close:hover { opacity: 1; }

/* ─── Spinner ────────────────────────────────────────────────────────── */

.fg-spinner {
  display: inline-flex; align-items: center; gap: var(--fg-space-2);
  padding: var(--fg-space-3);
  color: var(--fg-ink-muted); font-size: 13px;
}
.fg-spinner__svg { width: 18px; height: 18px; animation: fg-spin 0.8s linear infinite; }
.fg-spinner__svg circle { stroke: var(--fg-ink); stroke-dasharray: 90; stroke-dashoffset: 60; }
@keyframes fg-spin { to { transform: rotate(360deg); } }

/* ─── Transition entre étapes ────────────────────────────────────────── */

.fg-step-enter-active, .fg-step-leave-active {
  transition: opacity 200ms ease, transform 200ms cubic-bezier(0.16, 1, 0.3, 1);
}
.fg-step-enter-from { opacity: 0; transform: translateY(6px); }
.fg-step-leave-to { opacity: 0; transform: translateY(-6px); }

/* Apparition douce du bloc Tarif dès qu'une place est choisie (Results.vue) —
   simple fondu, sans translation : la carte suivante ne doit pas "sauter". */
.fg-fade-enter-active, .fg-fade-leave-active { transition: opacity 200ms ease; }
.fg-fade-enter-from, .fg-fade-leave-to { opacity: 0; }

/* ─── Overlay funnel (Résultats → Options → Booking) ─────────────────────
   Téléporté dans document.body : s'affranchit du conteneur hôte (largeur,
   overflow, backdrop-filter) qui écrasait le funnel. Plein écran, panel
   centré max 920px ; feuille plein écran sur mobile. */
.fg-overlay {
  position: fixed;
  /* Offset + z-index surchargeables par l'hôte (CSS vars) pour laisser un
     header fixe visible au-dessus. Défaut : plein écran. */
  top: var(--fg-overlay-top, 0);
  right: 0;
  bottom: 0;
  left: 0;
  z-index: var(--fg-overlay-z, 2147483000);
  /* Pleine page opaque, PAS une modale. Fond gris doux : les blocs-cartes
     blancs du funnel s'en détachent naturellement (réf. checkout Airbnb). */
  background: var(--fg-surface);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  font-family: var(--fg-font);
  color: var(--fg-ink);
  line-height: 1.5;
  -webkit-font-smoothing: antialiased;
  text-rendering: optimizeLegibility;
}
.fg-overlay *, .fg-overlay *::before, .fg-overlay *::after { box-sizing: border-box; }
.fg-overlay button { font-family: inherit; }
.fg-overlay__panel {
  width: 100%;
  max-width: 1040px;
  margin: 0 auto;
  padding: clamp(16px, 4vw, 40px);
  /* Espace haut plus généreux : écarte le funnel du header de l'hôte (logo LP)
     quand l'overlay démarre juste dessous (cf. --fg-overlay-top). */
  padding-top: clamp(28px, 5vw, 56px);
  /* Réserve l'espace du chrome bas de l'hôte (tab-bar) pour que la barre
     d'action sticky et le contenu ne soient pas masqués. */
  padding-bottom: calc(clamp(16px, 4vw, 40px) + var(--fg-overlay-bottom, 0px));
}
.fg-overlay-enter-active, .fg-overlay-leave-active { transition: opacity 180ms ease; }
.fg-overlay-enter-from, .fg-overlay-leave-to { opacity: 0; }

/* ─── Confirmation — scopé sous .fg-confirm (téléportable hors shadow DOM, zéro collision) ─── */
.fg-confirm {
/* ──────────────────────────────────────────────────────────────────────
 *  Confirmation — page central single-column
 *  Stack vertical de cards : Trajet · Places · Passagers · Véhicule ·
 *  Contact · Tarifs+CTA. Max-width 720px centré pour focus / scan.
 * ────────────────────────────────────────────────────────────────────── */

/* ──────────────────────────────────────────────────────────────────────
 *  Confirmation — palette stricte N&B + densité accrue (-25% padding).
 *  Aucune couleur d'accent (blue/orange/pink/red colorés) : tout en
 *  niveaux de gris + noir d'accent.
 * ────────────────────────────────────────────────────────────────────── */
  /* Tokens --c-* : ALIAS des tokens globaux --fg-* (même encre, mêmes gris,
     même typo) — la Confirmation ne doit pas vivre dans sa propre palette. */
  & {
  --c-text: var(--fg-ink, #101828);
  --c-text-2: var(--fg-ink-muted, #667085);
  --c-text-3: var(--fg-ink-faint, #98a2b3);
  --c-surface: var(--fg-paper, #ffffff);
  --c-surface-2: var(--fg-surface, #f7f8fa);
  --c-surface-3: var(--fg-line, #e7eaee);
  --c-border: var(--fg-line, #e7eaee);
  --c-border-strong: var(--fg-line-strong, #d0d5dd);
  --c-black: var(--fg-ink, #101828);

  max-width: 1040px;
  margin: 0 auto;
  padding: 0 0 40px;
  font-family: var(--fg-font);
}

/* ─── Empty state ──────────────────────────────────────── */
.fg-confirm__empty {
  display: flex; flex-direction: column;
  align-items: center; text-align: center;
  gap: 12px; padding: 48px 16px;
}
.fg-confirm__empty-icon {
  width: 48px; height: 48px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  background: var(--c-surface-2); color: var(--c-text-3);
}
.fg-confirm__empty-title { font-size: 15px; font-weight: 600; color: var(--c-text); margin: 0; }

/* Header : grammaire commune .fg-page__header (cf. section funnel) — le
   récapitulatif n'a plus de header spécifique. */
.fg-confirm > .fg-page__header { margin-bottom: 16px; }

/* ─── Stack : 2 colonnes (détails + récap sticky) sur large, pile sinon ── */
.fg-confirm__stack {
  display: grid;
  /* Même découpe 2/3 + 1/3 que .fg-page-with-summary (cohérence funnel). */
  grid-template-columns: minmax(0, 2fr) minmax(0, 1fr);
  gap: 16px;
  align-items: start;
}
.fg-confirm__main {
  display: flex; flex-direction: column;
  gap: 8px;
  min-width: 0;
}
.fg-confirm__aside {
  position: sticky;
  top: 12px;
}
@media (max-width: 860px) {
  .fg-confirm__stack { grid-template-columns: 1fr; gap: 8px; }
  .fg-confirm__aside { position: static; }
}

/* ─── Card de base ─────────────────────────────────────── */
.fg-card {
  background: var(--c-surface);
  border: 1px solid var(--c-border);
  border-radius: 10px;
  padding: 13px 15px;
}
.fg-card__head {
  display: flex; align-items: center; gap: 8px;
  padding-bottom: 10px; margin-bottom: 12px;
  border-bottom: 1px solid var(--c-border);
}
.fg-card__icon {
  color: var(--c-text-2);
  display: inline-flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.fg-card__title {
  margin: 0; font-size: 11.5px; font-weight: 700;
  color: var(--c-text); text-transform: uppercase; letter-spacing: 0.07em;
}
.fg-card__count {
  margin-left: auto;
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 20px; height: 20px; padding: 0 7px;
  border-radius: 5px;
  background: var(--c-text); color: var(--c-surface);
  font-size: 11px; font-weight: 700;
}
.fg-card__edit {
  margin-left: auto;
  background: none; border: 0;
  color: var(--c-text-2);
  font-size: 11px; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.05em;
  cursor: pointer; padding: 2px 2px;
  transition: color 0.15s ease;
}
.fg-card__count + .fg-card__edit { margin-left: 10px; }
.fg-card__edit:hover { color: var(--c-black); }
.fg-card__body { display: flex; flex-direction: column; gap: 12px; }
.fg-card__rows { gap: 6px; }

/* ─── Trajet (route horizontale grid 1fr auto 1fr) ─────── */
.fg-trip {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(70px, 1fr) minmax(0, 1fr);
  gap: 10px;
  align-items: center;
}
.fg-trip__port {
  font-size: 20px; font-weight: 800;
  color: var(--c-text);
  letter-spacing: -0.015em;
  word-break: break-word;
  hyphens: auto;
}
.fg-trip__port--from { text-align: left; }
.fg-trip__port--to { text-align: right; }
.fg-trip__line {
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;
}
.fg-trip__bar {
  flex: 1 1 auto;
  height: 1.5px;
  background: var(--c-border-strong);
  border-radius: 1px;
}
.fg-trip__dot {
  width: 8px; height: 8px;
  border-radius: 50%;
  background: var(--c-text);
  flex-shrink: 0;
}
.fg-trip__dot--to {
  background: var(--c-surface);
  border: 1.5px solid var(--c-text);
}
.fg-trip__arrow {
  color: var(--c-text-2);
  flex-shrink: 0;
}

.fg-trip__schedule {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  padding: 14px 16px;
  background: var(--c-surface-2);
  border: 1px solid var(--c-border);
  border-radius: 10px;
}
@media (max-width: 480px) { .fg-trip__schedule { grid-template-columns: 1fr; gap: 10px; } }
.fg-trip__schedule-col { display: flex; flex-direction: column; gap: 4px; }
.fg-trip__schedule-label {
  font-size: 10.5px; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.06em;
  color: #475569;  /* slate-600 — contraste renforcé */
}
.fg-trip__schedule-value {
  font-size: 13px; font-weight: 600; color: var(--c-text);
  /* Même famille typo que tout le funnel — pas de monospace ; les chiffres
     restent alignés via tabular-nums. */
  font-variant-numeric: tabular-nums;
}
.fg-trip__duration { color: var(--c-text-3); }
.fg-trip__meta {
  display: inline-flex; align-items: center; gap: 10px;
  font-size: 13px; color: var(--c-text-2);
  flex-wrap: wrap;
  margin-top: 4px;
}
.fg-trip__logo {
  width: 60px; height: 60px;
  border-radius: 8px;
  object-fit: contain;
  flex-shrink: 0;
  padding: 6px;
  background: var(--c-surface);
  border: 1px solid var(--c-border);
  filter: grayscale(1) contrast(1.05);
}
.fg-trip__sep { color: var(--c-text-3); }

/* ─── Grid de cells inline (Contact, Véhicule, Places) ── */
.fg-grid {
  display: grid;
  gap: 16px 24px;
}
.fg-grid--3 {
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
}
.fg-cell {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}
.fg-cell__label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #475569;  /* slate-600 — contraste renforcé vs ancien --c-text-2 */
}
.fg-cell__value {
  font-size: 14px;
  font-weight: 600;
  color: var(--c-text);
  word-break: break-word;
}

/* ─── Identité véhicule (logo marque + nom + taille) ───── */
.fg-veh-ident {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.fg-veh-ident__logo {
  width: 26px;
  height: 26px;
  object-fit: contain;
  border-radius: 5px;
  flex-shrink: 0;
}
.fg-veh-ident__badge {
  width: 26px;
  height: 26px;
  border-radius: 5px;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  line-height: 1;
  text-transform: uppercase;
}
.fg-veh-ident__main {
  display: inline-flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
  line-height: 1.25;
}
.fg-veh-ident__size {
  font-size: 11px;
  font-weight: 500;
  color: var(--c-text-2);
  font-variant-numeric: tabular-nums;
}

/* ─── KV rows (Places, Véhicule, Contact, Animaux) ─────── */
.fg-row {
  display: grid;
  grid-template-columns: 110px 1fr;
  gap: 12px;
  align-items: baseline;
  padding: 4px 0;
  font-size: 13px;
}
.fg-row + .fg-row {
  border-top: 1px dashed var(--c-border);
  padding-top: 8px;
}
.fg-row__label { color: var(--c-text-2); font-size: 12px; }
.fg-row__value { color: var(--c-text); font-weight: 500; word-break: break-word; }
.fg-mono {
  /* Historiquement monospace (email/tél/plaque) — aligné sur la famille
     unique du funnel, tabular-nums pour les suites de chiffres. */
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.01em;
}

/* ─── Passagers : 1 par row, horizontal forcé ──────────── */
/* Résumé final : en-tête de sous-groupe + installation fusionnée par groupe. */
.fg-group-head {
  font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em;
  color: var(--c-text-3); margin: 10px 0 2px;
}
.fg-group-acc {
  display: flex; flex-wrap: wrap; align-items: center; gap: 6px 12px;
  margin: 4px 0 8px; padding: 8px 10px;
  background: var(--c-surface-2, #f8fafc); border-radius: 8px;
  font-size: 12px; color: var(--c-text);
}
.fg-group-acc__icon { color: var(--c-text-3); flex-shrink: 0; }
.fg-group-acc__item { display: inline-flex; align-items: baseline; gap: 4px; }
.fg-group-acc__dir {
  font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em;
  color: var(--c-text-3);
}

.fg-pax {
  display: flex !important;
  flex-direction: row !important;
  flex-wrap: nowrap !important;
  align-items: center;
  gap: 14px;
  padding: 8px 0;
  min-height: 40px;
}
.fg-pax + .fg-pax {
  border-top: 1px solid var(--c-border);
  margin-top: 4px;
  padding-top: 14px;
}
.fg-pax__avatar {
  width: 34px; height: 34px;
  border-radius: 8px;
  background: var(--c-text);
  color: var(--c-surface);
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 12px; font-weight: 700;
  flex-shrink: 0;
}
.fg-pax__info {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
  min-width: 0;
  flex: 1 1 auto;
  flex-wrap: wrap;
}
.fg-pax__name {
  font-size: 14px;
  font-weight: 600;
  color: var(--c-text);
  flex: 1 1 auto;
  min-width: 0;
}
.fg-pax__meta {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

/* ─── Chip (B&W, contrasté) ────────────────────────────── */
.fg-chip {
  display: inline-flex; align-items: center;
  font-size: 11px; font-weight: 700;
  letter-spacing: 0.01em;
  padding: 3px 9px; border-radius: 999px;
  background: var(--c-text);          /* fond noir */
  color: var(--c-surface);            /* texte blanc */
  border: 1px solid var(--c-text);
  white-space: nowrap;
}
.fg-chip--adult,
.fg-chip--child,
.fg-chip--baby {
  background: var(--c-text);
  color: var(--c-surface);
  border: 1px solid var(--c-text);
}

/* ─── Checkout card (Tarifs + CTA) ─────────────────────── */
.fg-card--checkout { border-color: var(--c-border-strong); }

.fg-price {
  display: flex; flex-direction: column; gap: 6px;
  padding-bottom: 2px;
}
.fg-price__row {
  display: flex; justify-content: space-between; gap: 10px;
  font-size: 13px; color: var(--c-text);
}
/* Label = flux texte extensible (wrap propre des libellés repas longs) ; le
   montant reste insécable à droite (cf. .fg-price__amount). */
.fg-price__label {
  flex: 1; min-width: 0;
}
.fg-price__sub { color: var(--c-text-3); font-size: 11px; white-space: nowrap; }
/* Sous-prix par groupe (dossier > 4 pax) : retrait + ton atténué. */
.fg-price__row--sub { font-size: 11.5px; color: var(--c-text-3); padding-left: 10px; }
.fg-price__row--sub .fg-price__amount { font-weight: 500; }
.fg-price__sublabel { color: var(--c-text-3); }
/* Montant jamais coupé (« 97,00 € » sur une seule ligne) et non compressible. */
.fg-price__amount { font-weight: 600; font-variant-numeric: tabular-nums; white-space: nowrap; flex-shrink: 0; }
.fg-price__note { margin: 2px 0 0; font-size: 10.5px; color: var(--c-text-3); font-style: italic; }

.fg-total {
  display: flex; justify-content: space-between; align-items: baseline;
  padding: 10px 0 2px;
  margin-top: 4px;
  border-top: 1.5px solid var(--c-text);
}
.fg-total__label {
  font-size: 12px; font-weight: 600;
  text-transform: uppercase; letter-spacing: 0.05em;
  color: var(--c-text);
}
.fg-total__value {
  font-size: 20px; font-weight: 700;
  color: var(--c-text);
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.01em;
}

.fg-checkout__warning {
  margin: 10px 0 0;
  padding: 8px 10px;
  background: var(--c-surface-2);
  border: 1px solid var(--c-border);
  border-radius: 8px;
  font-size: 11.5px; color: var(--c-text);
  line-height: 1.4;
  display: flex; align-items: center; gap: 6px;
}

.fg-cta {
  position: relative;
  display: inline-flex; align-items: center; justify-content: center;
  gap: 8px; width: 100%; height: 48px;
  margin-top: 14px; padding: 0 16px;
  background: var(--c-black); color: #ffffff;
  border: 0; border-radius: 8px;
  font-size: 14px; font-weight: 700;
  letter-spacing: 0.01em;
  cursor: pointer;
  transition: transform 0.06s ease, background 0.15s ease;
}
.fg-cta:hover:not(.fg-cta--disabled) {
  background: #000000;
}
.fg-cta:active:not(.fg-cta--disabled) { transform: translateY(1px); }
.fg-cta--disabled {
  background: var(--c-surface-3) !important;
  color: var(--c-text-3) !important;
  cursor: not-allowed;
  pointer-events: none;
}
.fg-cta__label { white-space: nowrap; }
.fg-cta__spinner {
  width: 14px; height: 14px; border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #ffffff;
  animation: fg-spin 0.7s linear infinite;
  flex-shrink: 0;
}

.fg-checkout__newsearch {
  margin-top: 6px;
  background: none; border: 0;
  padding: 4px;
  color: var(--c-text-2);
  font-size: 12px; font-weight: 500;
  cursor: pointer;
  text-decoration: underline; text-underline-offset: 3px;
  text-align: center;
  width: 100%;
}
.fg-checkout__newsearch:hover:not(:disabled) { color: var(--c-text); }
.fg-checkout__newsearch:disabled { opacity: 0.4; cursor: not-allowed; }
}
